import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '@/App';
import { useTheme } from '@/contexts/ThemeContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Users, Building2, BarChart3, Database, Wifi, ChevronDown, ChevronUp, Shield, Search, RefreshCw, AlertTriangle, CheckCircle, Loader2, Server, HardDrive, Cpu, Activity, UserPlus, Trash2, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PLANS = ['free', 'starter', 'pro', 'enterprise'];

export default function AdminDashboard() {
  const { token } = useContext(AuthContext);
  const theme = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedUser, setExpandedUser] = useState(null);
  const [userDetails, setUserDetails] = useState({});
  const [search, setSearch] = useState('');
  const [syncing, setSyncing] = useState(null);
  const [syncResult, setSyncResult] = useState(null);
  const [serverStatus, setServerStatus] = useState(null);
  const [showServer, setShowServer] = useState(false);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', name: '', password: '', plan: 'pro' });
  const [creating, setCreating] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const loadData = () => {
    setLoading(true);
    axios.get(`${API}/admin/overview`, { headers })
      .then(r => { setData(r.data); setLoading(false); })
      .catch(e => { setError(e.response?.status === 403 ? 'No autorizado' : 'Error'); setLoading(false); });
  };

  const loadServer = () => {
    axios.get(`${API}/admin/server-status`, { headers })
      .then(r => setServerStatus(r.data)).catch(() => {});
  };

  useEffect(() => { loadData(); loadServer(); const iv = setInterval(loadServer, 30000); return () => clearInterval(iv); }, []);

  const toggleUser = async (userId) => {
    if (expandedUser === userId) { setExpandedUser(null); return; }
    setExpandedUser(userId);
    if (!userDetails[userId]) {
      try {
        const r = await axios.get(`${API}/admin/user/${userId}/details`, { headers });
        setUserDetails(prev => ({ ...prev, [userId]: r.data }));
      } catch(e) { console.error(e); }
    }
  };

  const changePlan = async (userId, plan) => {
    try {
      await axios.post(`${API}/admin/user/${userId}/change-plan`, { plan }, { headers });
      setData(prev => ({
        ...prev,
        users: prev.users.map(u => u.id === userId ? { ...u, plan } : u)
      }));
    } catch(e) { console.error(e); }
  };

  const forceSync = async (userId, userName) => {
    setSyncing(userId);
    setSyncResult(null);
    try {
      const r = await axios.post(`${API}/admin/user/${userId}/force-sync`, {}, { headers });
      setSyncResult({ userId, ...r.data });
      loadData();
    } catch(e) { setSyncResult({ userId, status: 'error', message: 'Error en sync' }); }
    setSyncing(null);
  };

  const deleteUser = async (userId, email) => {
    if (!window.confirm('¿Eliminar completamente al usuario ' + email + '? Se borrarán todos sus datos. Esta acción NO se puede deshacer.')) return;
    try {
      await axios.delete(API + '/admin/user/' + userId, { headers });
      loadData();
    } catch(e) { 
      console.error(e);
      alert('Error al eliminar: ' + (e.response?.data?.detail || e.message));
    }
  };

  const createUser = async () => {
    if (!newUser.email || !newUser.password) return;
    setCreating(true);
    try {
      await axios.post(API + '/admin/create-user', newUser, { headers });
      setShowCreateUser(false);
      setNewUser({ email: '', name: '', password: '', plan: 'pro' });
      loadData();
    } catch(e) { alert('Error: ' + (e.response?.data?.detail || e.message)); }
    setCreating(false);
  };

  if (loading) return (
    <DashboardLayout>
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-10 h-10 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></div>
      </div>
    </DashboardLayout>
  );

  if (error) return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto px-4 py-12 text-center">
        <Shield className="w-16 h-16 text-red-400 mx-auto mb-4" />
        <h1 className={`text-2xl font-bold ${theme.text} mb-2`}>{error}</h1>
        <p className={theme.textSecondary}>Solo administradores pueden acceder.</p>
      </div>
    </DashboardLayout>
  );

  const fmt = n => n >= 1000 ? `${(n/1000).toFixed(1)}K` : String(n || 0);
  const filtered = data.users.filter(u =>
    u.name?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase())
  ).sort((a,b) => new Date(b.created_at) - new Date(a.created_at));

  // Alerts
  const noClients = data.users.filter(u => u.clients_count === 0);
  const noMeta = data.users.filter(u => !u.meta_connected && u.clients_count > 0);
  const noCampaigns = data.users.filter(u => u.campaigns_count === 0 && u.clients_count > 0);

  // Revenue estimate
  const planPrices = { free: 0, starter: 19, pro: 49, enterprise: 149 };
  const mrr = data.users.reduce((s, u) => s + (planPrices[u.plan] || 0), 0);

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className={`text-2xl font-bold ${theme.text} flex items-center gap-2`}>
              <Shield className="w-6 h-6 text-indigo-500" /> Panel Admin
            </h1>
            <p className={theme.textSecondary}>Vista general de todas las cuentas</p>
          </div>
          <button onClick={loadData} className="flex items-center gap-2 px-4 py-2 bg-indigo-500 text-white rounded-lg text-sm font-semibold hover:bg-indigo-600 transition-colors">
            <RefreshCw className="w-4 h-4" /> Actualizar
          </button>
        </div>

        {/* Create User Modal */}
        {showCreateUser && (
          <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center'}}>
            <div style={{background:'white',borderRadius:16,padding:32,width:440,maxWidth:'90vw',position:'relative'}}>
              <button onClick={() => setShowCreateUser(false)} style={{position:'absolute',top:12,right:12,border:'none',background:'none',cursor:'pointer'}}><X size={20} color="#94a3b8" /></button>
              <h3 style={{fontSize:18,fontWeight:700,marginBottom:20}}>Crear Nuevo Usuario</h3>
              <div style={{display:'flex',flexDirection:'column',gap:12}}>
                <input placeholder="Email" value={newUser.email} onChange={e => setNewUser({...newUser, email: e.target.value})} style={{padding:'10px 14px',borderRadius:8,border:'1px solid #e2e8f0',fontSize:14}} />
                <input placeholder="Nombre" value={newUser.name} onChange={e => setNewUser({...newUser, name: e.target.value})} style={{padding:'10px 14px',borderRadius:8,border:'1px solid #e2e8f0',fontSize:14}} />
                <input placeholder="Contrasena" type="password" value={newUser.password} onChange={e => setNewUser({...newUser, password: e.target.value})} style={{padding:'10px 14px',borderRadius:8,border:'1px solid #e2e8f0',fontSize:14}} />
                <select value={newUser.plan} onChange={e => setNewUser({...newUser, plan: e.target.value})} style={{padding:'10px 14px',borderRadius:8,border:'1px solid #e2e8f0',fontSize:14}}>
                  <option value="free">free</option><option value="starter">starter</option><option value="pro">pro</option><option value="enterprise">enterprise</option>
                </select>
                <button onClick={createUser} disabled={creating} style={{padding:'12px',borderRadius:8,background:'#6366f1',color:'white',fontWeight:600,border:'none',cursor:'pointer',opacity:creating?0.5:1}}>
                  {creating ? 'Creando...' : 'Crear Usuario'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
          {[
            { label: 'Usuarios', value: data.total_users, icon: <Users className="w-5 h-5" />, color: 'text-blue-500' },
            { label: 'Clientes', value: data.total_clients, icon: <Building2 className="w-5 h-5" />, color: 'text-green-500' },
            { label: 'Campañas', value: data.total_campaigns, icon: <BarChart3 className="w-5 h-5" />, color: 'text-purple-500' },
            { label: 'Métricas', value: fmt(data.total_metrics_rows || 0), icon: <Database className="w-5 h-5" />, color: 'text-orange-500' },
            { label: 'Meta Conex.', value: data.total_meta_connections, icon: <Wifi className="w-5 h-5" />, color: 'text-pink-500' },
            { label: 'MRR Est.', value: `$${mrr}`, icon: <BarChart3 className="w-5 h-5" />, color: 'text-emerald-500' },
          ].map((s, i) => (
            <div key={i} className={`${theme.card} rounded-xl p-4 border border-gray-100`}>
              <div className={`flex items-center gap-2 mb-1 ${s.color}`}>{s.icon}<span className="text-xs font-medium uppercase tracking-wide">{s.label}</span></div>
              <p className={`text-2xl font-bold ${theme.text}`}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* Alerts */}
        {(noClients.length > 0 || noMeta.length > 0 || noCampaigns.length > 0) && (
          <div className="mb-6 space-y-2">
            {noClients.length > 0 && (
              <div className="flex items-center gap-3 bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm">
                <AlertTriangle className="w-4 h-4 text-yellow-500 flex-shrink-0" />
                <span className="text-yellow-700"><strong>{noClients.length} usuarios</strong> sin clientes: {noClients.map(u => u.email).join(', ')}</span>
              </div>
            )}
            {noMeta.length > 0 && (
              <div className="flex items-center gap-3 bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm">
                <AlertTriangle className="w-4 h-4 text-orange-500 flex-shrink-0" />
                <span className="text-orange-700"><strong>{noMeta.length} usuarios</strong> con clientes pero sin Meta conectado: {noMeta.map(u => u.email).join(', ')}</span>
              </div>
            )}
            {noCampaigns.length > 0 && (
              <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-lg p-3 text-sm">
                <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
                <span className="text-red-700"><strong>{noCampaigns.length} usuarios</strong> con clientes pero sin campañas: {noCampaigns.map(u => u.email).join(', ')}</span>
              </div>
            )}
          </div>
        )}

        {/* Sync Result */}
        {syncResult && (
          <div className={`mb-4 flex items-center gap-3 p-3 rounded-lg text-sm ${syncResult.status === 'ok' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
            <CheckCircle className={`w-4 h-4 ${syncResult.status === 'ok' ? 'text-green-500' : 'text-red-500'}`} />
            <span className={syncResult.status === 'ok' ? 'text-green-700' : 'text-red-700'}>
              {syncResult.status === 'ok' ? `Sync completado: ${syncResult.rows_synced} filas, ${syncResult.campaigns_updated} campañas` : syncResult.message}
            </span>
            <button onClick={() => setSyncResult(null)} className="ml-auto text-gray-400 hover:text-gray-600">✕</button>
          </div>
        )}

        {/* Server Monitor */}
        <div className={`${theme.card} rounded-xl border border-gray-100 mb-6 overflow-hidden`}>
          <div className="p-4 cursor-pointer hover:bg-gray-50 flex items-center justify-between" onClick={() => setShowServer(!showServer)}>
            <div className="flex items-center gap-2">
              <Server className="w-5 h-5 text-indigo-500" />
              <span className={`font-semibold ${theme.text}`}>Estado del Servidor</span>
              {serverStatus && (
                <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${
                  serverStatus.cpu.percent < 50 && serverStatus.memory.percent < 70 ? 'bg-green-100 text-green-700' :
                  serverStatus.cpu.percent < 80 && serverStatus.memory.percent < 85 ? 'bg-yellow-100 text-yellow-700' :
                  'bg-red-100 text-red-700'
                }`}>{serverStatus.cpu.percent < 50 && serverStatus.memory.percent < 70 ? 'Saludable' : serverStatus.cpu.percent < 80 ? 'Moderado' : 'Alerta'}</span>
              )}
            </div>
            {showServer ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
          </div>
          {showServer && serverStatus && (
            <div className="border-t border-gray-100 p-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                {/* CPU */}
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2"><Cpu className="w-4 h-4 text-blue-500" /><span className="text-xs font-semibold text-gray-500">CPU</span></div>
                  <p className={`text-xl font-bold ${serverStatus.cpu.percent > 80 ? 'text-red-500' : serverStatus.cpu.percent > 50 ? 'text-yellow-500' : 'text-green-500'}`}>{serverStatus.cpu.percent}%</p>
                  <div className="w-full h-2 bg-gray-200 rounded-full mt-2"><div className={`h-2 rounded-full ${serverStatus.cpu.percent > 80 ? 'bg-red-500' : serverStatus.cpu.percent > 50 ? 'bg-yellow-500' : 'bg-green-500'}`} style={{width: serverStatus.cpu.percent + '%'}}></div></div>
                  <p className="text-xs text-gray-400 mt-1">{serverStatus.cpu.cores} cores · Load: {serverStatus.cpu.load_avg.join(', ')}</p>
                </div>
                {/* Memory */}
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2"><Activity className="w-4 h-4 text-purple-500" /><span className="text-xs font-semibold text-gray-500">RAM</span></div>
                  <p className={`text-xl font-bold ${serverStatus.memory.percent > 85 ? 'text-red-500' : serverStatus.memory.percent > 70 ? 'text-yellow-500' : 'text-green-500'}`}>{serverStatus.memory.percent}%</p>
                  <div className="w-full h-2 bg-gray-200 rounded-full mt-2"><div className={`h-2 rounded-full ${serverStatus.memory.percent > 85 ? 'bg-red-500' : serverStatus.memory.percent > 70 ? 'bg-yellow-500' : 'bg-green-500'}`} style={{width: serverStatus.memory.percent + '%'}}></div></div>
                  <p className="text-xs text-gray-400 mt-1">{serverStatus.memory.used_gb} / {serverStatus.memory.total_gb} GB</p>
                </div>
                {/* Disk */}
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2"><HardDrive className="w-4 h-4 text-orange-500" /><span className="text-xs font-semibold text-gray-500">Disco</span></div>
                  <p className={`text-xl font-bold ${serverStatus.disk.percent > 90 ? 'text-red-500' : serverStatus.disk.percent > 70 ? 'text-yellow-500' : 'text-green-500'}`}>{serverStatus.disk.percent}%</p>
                  <div className="w-full h-2 bg-gray-200 rounded-full mt-2"><div className={`h-2 rounded-full ${serverStatus.disk.percent > 90 ? 'bg-red-500' : serverStatus.disk.percent > 70 ? 'bg-yellow-500' : 'bg-green-500'}`} style={{width: serverStatus.disk.percent + '%'}}></div></div>
                  <p className="text-xs text-gray-400 mt-1">{serverStatus.disk.used_gb} / {serverStatus.disk.total_gb} GB</p>
                </div>
                {/* Processes */}
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2"><Server className="w-4 h-4 text-green-500" /><span className="text-xs font-semibold text-gray-500">Procesos</span></div>
                  <div className="space-y-1.5">
                    {serverStatus.processes.map((p, i) => (
                      <div key={i} className="flex items-center justify-between">
                        <span className="text-xs font-medium text-gray-700">{p.name}</span>
                        <div className="flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${p.status === 'online' ? 'bg-green-400' : 'bg-red-400'}`}></span>
                          <span className="text-xs text-gray-400">{p.memory}MB</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-400 mt-2">TCP: {serverStatus.tcp_connections}</p>
                </div>
              </div>
              {/* Last sync & errors */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs font-semibold text-gray-500 mb-1">Último Sync</p>
                  <p className="text-xs text-gray-600">{serverStatus.last_sync}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs font-semibold text-gray-500 mb-1">Errores Recientes ({serverStatus.recent_errors.length})</p>
                  {serverStatus.recent_errors.length > 0 ? (
                    <div className="space-y-1 max-h-24 overflow-y-auto">{serverStatus.recent_errors.map((e, i) => <p key={i} className="text-xs text-red-500 truncate">{e}</p>)}</div>
                  ) : <p className="text-xs text-green-500">Sin errores</p>}
                </div>
              </div>
              <p className="text-xs text-gray-300 mt-3 text-right">Auto-refresh cada 30s</p>
            </div>
          )}
        </div>

        {/* Search */}
        <div className="flex items-center gap-4 mb-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input type="text" placeholder="Buscar por nombre o email..." value={search} onChange={e => setSearch(e.target.value)}
              className={`w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-200 text-sm ${theme.text} bg-white focus:outline-none focus:ring-2 focus:ring-indigo-300`} />
          </div>
          <span className="text-sm text-gray-400">{filtered.length} usuarios</span>
        </div>

        {/* Users List */}
        <div className="space-y-3">
          {filtered.map(user => (
            <div key={user.id} className={`${theme.card} rounded-xl border border-gray-100 overflow-hidden`}>
              <div className="p-4 cursor-pointer hover:bg-gray-50 transition-colors flex items-center justify-between"
                onClick={() => toggleUser(user.id)}>
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm ${
                    user.plan === 'enterprise' ? 'bg-indigo-600' : user.plan === 'pro' ? 'bg-blue-500' : user.plan === 'starter' ? 'bg-green-500' : 'bg-gray-400'
                  }`}>{user.name?.charAt(0)?.toUpperCase() || '?'}</div>
                  <div>
                    <p className={`font-semibold ${theme.text}`}>{user.name || 'Sin nombre'}</p>
                    <p className="text-sm text-gray-400">{user.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  {/* Plan selector */}
                  <select value={user.plan || 'free'} onClick={e => e.stopPropagation()}
                    onChange={e => { e.stopPropagation(); changePlan(user.id, e.target.value); }}
                    className={`text-xs px-2 py-1 rounded-full font-semibold border-0 cursor-pointer ${
                      user.plan === 'enterprise' ? 'bg-indigo-100 text-indigo-700' :
                      user.plan === 'pro' ? 'bg-blue-100 text-blue-700' :
                      user.plan === 'starter' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                    {PLANS.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                  <span className="text-sm text-gray-400 hidden md:inline">{user.clients_count} clientes</span>
                  <span className="text-sm text-gray-400 hidden md:inline">{user.campaigns_count} camp.</span>
                  <span className={`w-2 h-2 rounded-full ${user.meta_connected ? 'bg-green-400' : 'bg-gray-300'}`}></span>
                  {/* Sync button */}
                  <button onClick={e => { e.stopPropagation(); forceSync(user.id, user.name); }}
                    disabled={syncing === user.id}
                    className="p-1.5 rounded-lg hover:bg-indigo-50 transition-colors disabled:opacity-50" title="Forzar Sync">
                    {syncing === user.id ? <Loader2 className="w-4 h-4 animate-spin text-indigo-500" /> : <RefreshCw className="w-4 h-4 text-indigo-400" />}
                  </button>
                  <button onClick={e => { e.stopPropagation(); deleteUser(user.id, user.email); }}
                    className="p-1.5 rounded-lg hover:bg-red-50 transition-colors" title="Desactivar">
                    <Trash2 className="w-4 h-4 text-red-300 hover:text-red-500" />
                  </button>
                  {expandedUser === user.id ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                </div>
              </div>

              {expandedUser === user.id && (
                <div className="border-t border-gray-100 p-4 bg-gray-50">
                  {!userDetails[user.id] ? (
                    <div className="flex items-center gap-2 text-sm text-gray-400"><Loader2 className="w-4 h-4 animate-spin" /> Cargando...</div>
                  ) : (
                    <div>
                      <div className="flex gap-4 text-xs text-gray-400 mb-3">
                        <span>Registrado: {new Date(userDetails[user.id].created_at).toLocaleDateString('es-CL')}</span>
                        <span>ID: {user.id.slice(0,8)}...</span>
                      </div>
                      {userDetails[user.id].clients.length === 0 ? (
                        <p className="text-sm text-gray-400 italic">Sin clientes configurados</p>
                      ) : (
                        <div className="space-y-4">
                          {userDetails[user.id].clients.map(client => (
                            <div key={client.id} className="bg-white rounded-lg p-4 border border-gray-200">
                              <div className="flex items-center justify-between mb-3">
                                <h4 className="font-semibold text-gray-800">{client.name}</h4>
                                <div className="flex items-center gap-3 text-xs text-gray-400">
                                  <span>{client.campaigns.length} campañas</span>
                                  <span>{client.metrics_rows} métricas</span>
                                  {client.meta_connection && (
                                    <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">Meta: {client.meta_connection.meta_user_name || '✓'}</span>
                                  )}
                                  {client.meta_connection?.facebook_page_id && <span className="bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">FB Page ✓</span>}
                                  {client.meta_connection?.instagram_account_id && <span className="bg-pink-50 text-pink-600 px-2 py-0.5 rounded-full">IG ✓</span>}
                                </div>
                              </div>
                              {client.campaigns.length > 0 && (
                                <table className="w-full text-xs">
                                  <thead>
                                    <tr className="text-gray-400 border-b">
                                      <th className="text-left py-1">Campaña</th>
                                      <th className="text-left py-1">Plat.</th>
                                      <th className="text-right py-1">Gasto</th>
                                      <th className="text-right py-1">Imp.</th>
                                      <th className="text-right py-1">Clicks</th>
                                      <th className="text-right py-1">Conv.</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {client.campaigns.map((camp, i) => (
                                      <tr key={i} className="border-b border-gray-50">
                                        <td className="py-1.5 text-gray-700 max-w-[200px] truncate">{camp.name}</td>
                                        <td className="py-1.5"><span className={`px-1.5 py-0.5 rounded text-xs ${camp.platform === 'meta' ? 'bg-blue-50 text-blue-600' : 'bg-red-50 text-red-600'}`}>{camp.platform}</span></td>
                                        <td className="py-1.5 text-right text-gray-700">${(camp.spent || 0).toLocaleString()}</td>
                                        <td className="py-1.5 text-right text-gray-700">{fmt(camp.impressions || 0)}</td>
                                        <td className="py-1.5 text-right text-gray-700">{fmt(camp.clicks || 0)}</td>
                                        <td className="py-1.5 text-right text-green-600 font-semibold">{camp.conversions || 0}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
}
